import { useState, useEffect, useCallback, useRef } from "react";
import { useRealtimeContext } from "../context/RealtimeContext";
import { getCurrentDemoConfig } from "../config/demoSites";
import type { SessionContext } from "../types/sessionContext";
import { throttledLog } from "../utils/throttledLog";

export default function useFormManager() {
    const { updateSessionContext, latestToolResponse } = useRealtimeContext();

    // Track updates that are in progress to prevent loops
    const [updateInProgress, setUpdateInProgress] = useState(false);

    // Keep track of field values to prevent redundant updates
    const fieldValuesRef = useRef<Record<string, Record<string, string>>>({});

    // Add a ref to track the last processed tool response
    const lastProcessedToolRef = useRef("");

    // Handle form field updates from any source
    const updateFormField = useCallback(
        (stepId: number, field: string, value: string, source: string) => {
            const stepKey = String(stepId);

            // Initialize field tracking if needed
            if (!fieldValuesRef.current[stepKey]) {
                fieldValuesRef.current[stepKey] = {};
            }

            // Check if this is a redundant update (same value)
            if (fieldValuesRef.current[stepKey][field] === value) {
                throttledLog("FORM_SKIP", `Skipping redundant update for "${field}" in step ${stepId}: "${value}"`);
                return;
            }

            // Prevent nested updates
            if (updateInProgress) {
                throttledLog("FORM_SKIP", `Skipping update while another is in progress: "${field}" in step ${stepId}`);
                return;
            }

            setUpdateInProgress(true);
            throttledLog("FORM_UPDATE", `Field "${field}" in step ${stepId}: "${value}" (from: ${source})`);

            // Store the new value to prevent redundant updates
            fieldValuesRef.current[stepKey][field] = value;

            // Update the context
            updateSessionContext((prev: SessionContext) => {
                const updated = structuredClone(prev);
                const stepKey = String(stepId);

                // Ensure the path exists
                if (!updated?.layout?.workflow?.steps?.[stepKey]?.fields) {
                    console.error(`[FORM_UPDATE] Invalid path for step ${stepId}`);
                    return prev;
                }

                // Skip if value hasn't changed in the context
                if (updated.layout.workflow.steps[stepKey].fields[field] === value) {
                    return prev; // No change needed
                }

                // Update the field
                updated.layout.workflow.steps[stepKey].fields[field] = value;

                return updated;
            }, `form-update-${source}`);

            // Wait for state update to complete before dispatching event
            setTimeout(() => {
                // Add visual feedback (dispatch custom event for UI components to react)
                // But only if we're not in an event-listener source (to break the loop)
                if (source !== "event-listener") {
                    document.dispatchEvent(
                        new CustomEvent("formFieldUpdated", {
                            detail: {
                                field,
                                value,
                                stepId,
                                source: "form-manager" // Always mark events from here as "form-manager"
                            },
                            bubbles: true,
                            cancelable: true
                        })
                    );
                }
                setUpdateInProgress(false);
            }, 0);
        },
        [updateSessionContext]
    );

    // Process tool responses centrally
    useEffect(() => {
        if (!latestToolResponse) return;

        // Create a unique identifier for this tool response to prevent reprocessing
        const responseId = `${latestToolResponse.tool_name}-${latestToolResponse.previous_item_id}-${latestToolResponse.tool_result}`;

        // Skip if we've already processed this exact tool response
        if (responseId === lastProcessedToolRef.current) {
            console.log("[useFormsManager] Skipping duplicate tool response");
            return;
        }

        // Update ref with current response ID
        lastProcessedToolRef.current = responseId;

        if (latestToolResponse.tool_name === "update_form_field") {
            try {
                const parsedResult = JSON.parse(latestToolResponse.tool_result);
                const { field, value } = parsedResult;

                // Log explicitly for each field type to aid debugging
                console.log(`[useFormsManager] Processing ${field} update to "${value}"`);

                // Find which step this field belongs to based on configuration
                const demoConfig = getCurrentDemoConfig();
                let targetStepId = null;

                // Find the step that contains this field
                for (const step of demoConfig.steps) {
                    if (step.fields.some(f => f.fieldName === field)) {
                        targetStepId = step.id;
                        break;
                    }
                }

                if (!targetStepId) {
                    console.error(`[useFormsManager] Field "${field}" not found in any step configuration`);
                    return;
                }

                // Update the field through our central method
                updateFormField(targetStepId, field, value, "tool-response");

                // Non-throttled log to ensure we see each update
                console.log(`[useFormsManager] Field "${field}" updated to "${value}" in step ${targetStepId}`);
            } catch (error) {
                console.error("[useFormsManager] Error processing tool response:", error);
            }
        }
    }, [latestToolResponse, updateFormField]);

    // Global function for external updates
    useEffect(() => {
        // Make the update function globally available for external systems
        window._lastFormFieldUpdate = (field: string, value: string) => {
            // Find appropriate step for this field
            const demoConfig = getCurrentDemoConfig();
            let targetStepId = null;

            // Find the step that contains this field
            for (const step of demoConfig.steps) {
                if (step.fields.some(f => f.fieldName === field)) {
                    targetStepId = step.id;
                    break;
                }
            }

            if (!targetStepId) {
                console.error(`[GLOBAL_UPDATE] Field "${field}" not found in any step configuration`);
                return;
            }

            updateFormField(targetStepId, field, value, "global-update");
            console.log(`[GLOBAL_UPDATE] Field "${field}" updated to "${value}" in step ${targetStepId}`);
        };

        // Setup listener for form field updated events from other components
        const handleFormFieldUpdate = (event: Event) => {
            const customEvent = event as CustomEvent;
            const { field, value, stepId, source } = customEvent.detail;

            // Skip events from ourselves to prevent loops
            if (source === "form-manager") {
                return;
            }

            // Also skip if the event came from useRealtime (which uses window._lastFormFieldUpdate)
            // which we already process directly above
            if (source === "useRealtime") {
                return;
            }

            // Direct UI updates (from user typing) should still be processed
            updateFormField(stepId, field, value, "event-listener");
        };

        document.addEventListener("formFieldUpdated", handleFormFieldUpdate);

        return () => {
            delete window._lastFormFieldUpdate;
            document.removeEventListener("formFieldUpdated", handleFormFieldUpdate);
        };
    }, [updateFormField]);

    return { updateFormField };
}
